const EFFECTIVE_DATE = "October 11, 2025";

const Card: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="bg-white dark:bg-slate-800 border border-gray-100 dark:border-slate-700 rounded-2xl shadow-sm p-6">
    {children}
  </div>
);
export const CancellationRefundPolicy: React.FC = () => {
  return (
    <div className="max-w-4xl mx-auto mt-20 mb-20 p-6 sm:p-8">
      <h1 className="text-3xl font-semibold mb-4">
        Cancellation & Refund Policy
      </h1>
      <p className="text-sm text-gray-600 mb-6">
        Effective date: <strong>{EFFECTIVE_DATE}</strong>
      </p>

      <Card>
        <section className="mb-6">
          <h2 className="text-xl font-medium mb-2">Order cancellation</h2>
          <p className="text-gray-700 leading-relaxed">
            • You can cancel an order within <strong>2 days (48 hours)</strong>{" "}
            from the time it was placed. To cancel, contact us at{" "}
            <a href="mailto:info@fleeby.in" className="underline">
              info@fleeby.in
            </a>{" "}
            or call/WhatsApp{" "}
            <a href="tel:+917778461609" className="underline">
              +91 97784 61609
            </a>
            . Please include your order number.
          </p>
          <p className="text-gray-700 leading-relaxed mt-2">
            • After 48 hours, orders cannot be cancelled. This is because we
            begin processing and preparing orders quickly — especially
            handcrafted items like resin art.
          </p>
        </section>

        <section className="mb-6">
          <h2 className="text-xl font-medium mb-2">
            Resin art / Custom products
          </h2>
          <p className="text-gray-700 leading-relaxed">
            • Custom resin art orders require 1–2 weeks to produce. While
            cancellation is allowed within the 48‑hour window, once production
            starts we may not be able to cancel or refund the full amount. If
            you request cancellation after production has materially begun, we
            will review the request and may offer partial refund or store credit
            depending on the stage of production.
          </p>
        </section>

        <section className="mb-6">
          <h2 className="text-xl font-medium mb-2">
            Returns (damaged / incorrect items)
          </h2>
          <p className="text-gray-700 leading-relaxed">
            • We accept returns only when the product arrives damaged, broken,
            or materially different from the order. To make a return claim you
            must provide{" "}
            <strong>video proof recorded at the time of delivery</strong>{" "}
            showing the packaging and the damaged item. Without clear video
            evidence captured at delivery, return requests will not be accepted.
          </p>
          <p className="text-gray-700 leading-relaxed mt-2">
            • If your return is accepted, you can choose either a replacement or
            a refund.
          </p>
        </section>

        <section className="mb-6">
          <h2 className="text-xl font-medium mb-2">Refund processing</h2>
          <p className="text-gray-700 leading-relaxed">
            • Refunds are processed within <strong>3–5 business days</strong>{" "}
            after we accept the return or cancellation. The exact timing for the
            refunded amount to appear in your account depends on your bank or
            payment provider.
          </p>
          <p className="text-gray-700 leading-relaxed mt-2">
            • For UPI payments done over WhatsApp, refunds (if applicable) may
            be processed back to the original UPI transaction or we may offer
            store credit — we will confirm the refund route case-by-case.
          </p>
        </section>

        <section className="mb-6">
          <h2 className="text-xl font-medium mb-2">
            How to request a cancellation / return / refund
          </h2>
          <ol className="list-decimal ml-5 mt-2 text-gray-700 space-y-2">
            <li>
              Reach out via email{" "}
              <a href="mailto:info@fleeby.in" className="underline">
                info@fleeby.in
              </a>{" "}
              or WhatsApp/Phone{" "}
              <a href="tel:+917778461609" className="underline">
                +91 97784 61609
              </a>{" "}
              and provide your order ID, name and a brief description.
            </li>
            <li>
              If requesting a return for damage, attach the delivery-time video
              proof. We will review and respond within 24–48 hours.
            </li>
            <li>
              After approval, follow the return instructions provided by our
              support team for shipping the item back (if applicable).
            </li>
          </ol>
        </section>

        <section className="mb-6">
          <h2 className="text-xl font-medium mb-2">Notes & exceptions</h2>
          <ul className="list-disc ml-5 mt-2 text-gray-700 space-y-2">
            <li>
              We do not accept returns for change-of-mind or buyer's remorse
              unless the item is defective on arrival.
            </li>
            <li>
              Products that are sold as final-sale or marked "non-returnable" at
              checkout cannot be returned unless damaged.
            </li>
            <li>
              We may require additional information for verification of claims
              to avoid fraudulent requests.
            </li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-medium mb-2">Contact & support</h2>
          <p className="text-gray-700 mb-2">
            For cancellations, returns, or refund queries:
          </p>
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="flex-1">
              <p className="text-sm text-gray-600">
                Email:{" "}
                <a href="mailto:info@fleeby.in" className="underline">
                  info@fleeby.in
                </a>
              </p>
              <p className="text-sm text-gray-600">
                Phone/WhatsApp:{" "}
                <a href="tel:+917778461609" className="underline">
                  +91 97784 61609
                </a>
              </p>
            </div>
            <div className="text-sm text-gray-500">
              We are a small team — expect personalised support and quick
              replies.
            </div>
          </div>
        </section>
      </Card>

      <p className="text-xs text-gray-500 mt-4">
        Policy updates: Fleeby may update these policies occasionally. The
        "Effective date" above shows when this version was published.
      </p>
    </div>
  );
};
